import React, { useEffect, useRef, useState } from 'react';
import { styled, Box } from '@mui/material';

import SegmentedPill, { PillProps } from '../pill';

type PillSize = 'sm' | 'md' | 'lg';

interface SegmentedSwitchOption<T> extends PillProps {
  value: T;
}

interface SegmentedSwitchProps<T> {
  value: T;
  setValue: (val: T) => void;
  size?: PillSize;
  options: SegmentedSwitchOption<T>[];
  style?: React.CSSProperties;
  /** Stretch to the container, splitting the width equally between options. */
  fullWidth?: boolean;
}

const Wrapper = styled(Box)({
  height: 'fit-content',
  width: 'fit-content',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  background: '#fff',
  borderRadius: 999,
  border: '2px solid #f3f5f7',
  padding: '8px',
  boxSizing: 'border-box',
  transition: 'all 0.3s ease'
});

const Slider = styled(Box)({
  position: 'absolute',
  top: 4,
  bottom: 4,
  borderRadius: 999,
  background: '#EBF5FF',
  transition: 'left 0.3s ease, width 0.3s ease'
});

const SegmentedSwitch = <T,>({
  value,
  setValue,
  size = 'md',
  options,
  style,
  fullWidth = false
}: SegmentedSwitchProps<T>) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [slider, setSlider] = useState({ left: 0, width: 0 });
  const [activeValue, setActiveValue] = useState<T | null>(value);

  useEffect(() => {
    const index = options.findIndex(opt => opt.value === value);
    const el = optionRefs.current[index];
    if (el && wrapperRef.current) {
      setSlider({ left: el.offsetLeft, width: el.offsetWidth });
    }
    if (activeValue !== value) {
      setActiveValue(null);
    }
  }, [value, options, activeValue]);

  useEffect(() => {
    const sliderEl = sliderRef.current;
    if (!sliderEl) {
      setActiveValue(value);
    } else {
      const handleTransitionEnd = () => setActiveValue(value);
      sliderEl.addEventListener('transitionend', handleTransitionEnd);
      return () =>
        sliderEl.removeEventListener('transitionend', handleTransitionEnd);
    }

    return () => {};
  }, [value]);

  return (
    <Wrapper
      ref={wrapperRef}
      sx={{ ...(fullWidth && { width: '100%' }), ...style }}>
      <Slider ref={sliderRef} sx={{ left: slider.left, width: slider.width }} />
      {options?.map((opt, idx) => (
        <Box
          // eslint-disable-next-line no-return-assign
          ref={(el: HTMLDivElement | null) => (optionRefs.current[idx] = el)}
          key={opt.value?.toString()}
          sx={{
            cursor: 'pointer',
            position: 'relative',
            zIndex: 1,
            ...(fullWidth && {
              flex: 1,
              minWidth: 0,
              // border-box, or the pill's padding pushes it past its segment.
              '& > div': { width: '100%', boxSizing: 'border-box' }
            })
          }}
          onClick={() => setValue(opt.value)}>
          <SegmentedPill
            label={opt.label}
            active={opt.value === activeValue}
            size={size}
            {...opt}
          />
        </Box>
      ))}
    </Wrapper>
  );
};

export default SegmentedSwitch;
